package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Button as HtmlButton

private const val ENTER_KEY = "Enter"
private const val BACKSPACE_KEY = "Backspace"

@Composable
internal fun ReleasesRemovableChip(label: String, removeLabel: String, onRemove: () -> Unit) {
    Span(
        attrs = Modifier
            .fontSize(Typography.bodySmall.size)
            .color(Palette.textPrimary)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", Space.s1.toString())
                property("padding", "0 ${Space.s1} 0 ${Spacing.xs}")
                property("min-height", "${Space.s7}")
                property("background-color", Palette.surfaceSubtle.cssRgb())
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("border-radius", "${Radius.pill}")
                property("overflow-wrap", "anywhere")
            }
            .toAttrs(),
    ) {
        Text(label)
        HtmlButton(
            attrs = Modifier
                .color(Palette.textSecondary)
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("min-width", "${Space.s6}")
                    property("min-height", "${Space.s6}")
                    property("background", "none")
                    property("border", "none")
                    property("border-radius", "${Radius.pill}")
                    property("cursor", "pointer")
                }
                .toAttrs {
                    attr("type", "button")
                    attr("aria-label", removeLabel)
                    onClick { onRemove() }
                },
        ) { LucideX(size = IconSize.sm.value.toInt()) }
    }
}

@Composable
internal fun ReleasesLabelInput(
    labels: List<String>,
    draft: String,
    onLabels: (List<String>) -> Unit,
    onDraft: (String) -> Unit,
) {
    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", Spacing.xs.toString())
            }
            .toAttrs(),
    ) {
        if (labels.isNotEmpty()) {
            Div(
                attrs = Modifier
                    .styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("gap", Spacing.xs.toString())
                    }
                    .toAttrs(),
            ) {
                labels.forEach { label ->
                    ReleasesRemovableChip(
                        label = label,
                        removeLabel = "Remove label $label",
                        onRemove = { onLabels(ReleasesLabels.remove(labels, label)) },
                    )
                }
            }
        }
        TextInput(
            value = draft,
            onValueChange = { text ->
                val (next, rest) = ReleasesLabels.consume(labels, text)
                if (next != labels) onLabels(next)
                onDraft(rest)
            },
            placeholder = "Add a label",
            ariaLabel = "Hold-back labels",
            clearable = false,
            modifier = Modifier.fillMaxWidth().onKeyDown { event ->
                when {
                    event.key == ENTER_KEY && draft.isNotBlank() -> {
                        event.preventDefault()
                        onLabels(ReleasesLabels.add(labels, draft))
                        onDraft("")
                    }
                    event.key == BACKSPACE_KEY && draft.isEmpty() && labels.isNotEmpty() ->
                        onLabels(ReleasesLabels.removeLast(labels))
                }
            },
        )
        val suggestions = ReleasesLabels.suggestions(labels)
        if (suggestions.isNotEmpty()) {
            Div(
                attrs = Modifier
                    .styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("align-items", "center")
                        property("gap", Spacing.xxs.toString())
                    }
                    .toAttrs(),
            ) {
                Span(attrs = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary).toAttrs()) { Text("Suggested") }
                suggestions.forEach { label ->
                    Button(
                        label = "+ $label",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { onLabels(ReleasesLabels.add(labels, label)) },
                    )
                }
            }
        }
    }
}
